/**
 * 홈 화면 상태 (MASTER §19, UX_FLOW §3): 사용자가 "어디까지 했더라?"를 기억하지 않아도 되게 한다.
 * 저장된 상태에서 순수하게 파생한다 (별도 할 일 목록을 저장하지 않음 → 불일치가 생기지 않는다).
 */
import type { Client, CounselingJournal, NextSessionBrief, Session } from '../schema/v1';

export type PendingTask = {
  sessionId: string;
  clientId: string;
  clientCode: string;
  sessionNumber: number;
  sessionDate: string;
  createdAt: string;
  stage: string;
  action: '계속' | '확인' | '승인';
  path: string;
};

export type PrepareCard = {
  clientId: string;
  clientCode: string;
  briefId: string;
  targetSessionNumber: number;
  lastSessionDate: string | null;
  objectives: string[];
  checks: string[];
  hasRiskFollowUp: boolean;
};

export type RecentItem = {
  sessionId: string;
  clientCode: string;
  sessionNumber: number;
  sessionDate: string;
  createdAt: string;
};

export function sessionStage(s: Session): { stage: string; action: PendingTask['action']; path: string } {
  const p = s.processingState;
  switch (s.status) {
    case 'draft':
      return { stage: p.filesSaved ? '처리 전' : '자료 입력 중', action: '계속', path: `/sessions/${s.id}` };
    case 'processing':
      return { stage: '처리 중 (이어서 진행)', action: '계속', path: `/sessions/${s.id}` };
    case 'verification':
      return { stage: '중요 전사 확인 필요', action: '확인', path: `/sessions/${s.id}/verify` };
    case 'ai_pending':
      return { stage: '분석 입력 전', action: '계속', path: `/sessions/${s.id}` };
    case 'review':
      return { stage: '상담기록지 승인 대기', action: '승인', path: `/sessions/${s.id}/journal` };
    case 'approved':
      return { stage: '완료', action: '계속', path: `/sessions/${s.id}` };
  }
}

export function deriveHome(input: {
  clients: Client[];
  sessions: Session[];
  journals: CounselingJournal[];
  briefs: NextSessionBrief[];
}): { prepare: PrepareCard[]; pending: PendingTask[]; recent: RecentItem[]; duplicates: Duplicate[] } {
  const code = new Map(input.clients.map((c) => [c.id, c.clientCode]));
  const active = new Set(input.clients.filter((c) => c.status === 'active').map((c) => c.id));
  const live = input.sessions.filter((s) => !s.deletedAt && code.has(s.clientId));

  const pending = live
    .filter((s) => s.status !== 'approved')
    .sort((a, b) => a.sessionDate.localeCompare(b.sessionDate) || a.sessionNumber - b.sessionNumber)
    .map((s) => ({
      sessionId: s.id,
      clientId: s.clientId,
      clientCode: code.get(s.clientId)!,
      sessionNumber: s.sessionNumber,
      sessionDate: s.sessionDate,
      createdAt: s.createdAt,
      ...sessionStage(s),
    }));

  const recent = live
    .filter((s) => s.status === 'approved')
    .sort((a, b) => b.sessionDate.localeCompare(a.sessionDate) || b.sessionNumber - a.sessionNumber)
    .slice(0, 5)
    .map((s) => ({
      sessionId: s.id,
      clientCode: code.get(s.clientId)!,
      sessionNumber: s.sessionNumber,
      sessionDate: s.sessionDate,
      createdAt: s.createdAt,
    }));

  const prepare: PrepareCard[] = [];
  for (const clientId of active) {
    const brief = latestUnusedBrief(input.briefs.filter((b) => b.clientId === clientId && !b.deletedAt));
    if (!brief) continue;
    const last = live
      .filter((s) => s.clientId === clientId && s.status === 'approved')
      .sort((a, b) => b.sessionDate.localeCompare(a.sessionDate))[0];
    prepare.push({
      clientId,
      clientCode: code.get(clientId)!,
      briefId: brief.id,
      targetSessionNumber: brief.targetSessionNumber,
      lastSessionDate: last?.sessionDate ?? null,
      objectives: (brief.sessionObjectives ?? []).slice(0, 2),
      checks: (brief.assessmentQuestions ?? []).map((q) => q.question).slice(0, 2),
      hasRiskFollowUp: (brief.riskFollowUp ?? []).length > 0,
    });
  }
  prepare.sort((a, b) => (a.lastSessionDate ?? '').localeCompare(b.lastSessionDate ?? ''));
  return { prepare, pending, recent, duplicates: findDuplicates(input.clients, input.sessions) };
}

/**
 * 두 기기에서 오프라인으로 같은 번호의 회기나 같은 코드의 내담자를 만들면 동기화 후 둘 다 남는다.
 * 자동으로 합치지 않고(어느 쪽이 맞는지 앱이 판단할 수 없음) 사용자에게 알린다.
 */
export type Duplicate = {
  kind: 'session' | 'client';
  clientId: string;
  clientCode: string;
  sessionNumber?: number;
  ids: string[];
};

export function findDuplicates(clients: Client[], sessions: Session[]): Duplicate[] {
  const out: Duplicate[] = [];
  const live = clients.filter((c) => !c.deletedAt);
  const byCode = new Map<string, Client[]>();
  for (const c of live) byCode.set(c.clientCode, [...(byCode.get(c.clientCode) ?? []), c]);
  for (const [code, cs] of byCode)
    if (cs.length > 1) out.push({ kind: 'client', clientId: cs[0]!.id, clientCode: code, ids: cs.map((c) => c.id) });
  const code = new Map(live.map((c) => [c.id, c.clientCode]));
  const byNum = new Map<string, Session[]>();
  for (const s of sessions) {
    if (s.deletedAt || !code.has(s.clientId)) continue;
    const k = `${s.clientId}|${s.sessionNumber}`;
    byNum.set(k, [...(byNum.get(k) ?? []), s]);
  }
  for (const ss of byNum.values())
    if (ss.length > 1)
      out.push({
        kind: 'session',
        clientId: ss[0]!.clientId,
        clientCode: code.get(ss[0]!.clientId)!,
        sessionNumber: ss[0]!.sessionNumber,
        ids: ss.map((s) => s.id),
      });
  return out;
}

export function latestUnusedBrief(briefs: NextSessionBrief[]): NextSessionBrief | undefined {
  return briefs
    .filter((b) => b.status !== 'used')
    .sort((a, b) => b.targetSessionNumber - a.targetSessionNumber || b.createdAt.localeCompare(a.createdAt))[0];
}

/** 다음 회기 번호 제안: 삭제되지 않은 회기 중 최대 + 1 */
export function suggestSessionNumber(sessions: Session[]): number {
  return sessions.filter((s) => !s.deletedAt).reduce((m, s) => Math.max(m, s.sessionNumber), 0) + 1;
}
